import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import StakeLoading from "@/components/StakeLoading";
import NoData from "@/components/NoData";
import LobbyMatchLeagues from "../LobbyMatchLeagues";
import LobbyMatch from "../LobbyMatch";
import LobbyDateTabs from "../LobbyDateTabs";
import LobbyLeagueFilterV9 from "../LobbyLeagueFilterV9";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import bgHome from "@/assets/images/bg-home.webp";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV9 = () => {
  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorOnPrimary,
    colorBorder,
    colorTextMuted,
    colorAccent,
    colorForeground60,
    colorTabHover,
    hasDataConfig,
  } = useCompConfig();
  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorForeground60 != null && { "--theme-foreground-60": colorForeground60 }),
        ...(colorTabHover != null && { "--theme-tab-hover": colorTabHover }),
      }
    : undefined;

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status = selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    // Stringify IDs for type-safe comparison
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  const visibleMatches = useMemo(() => {
    return allFilteredMatches.slice(0, displayLimit);
  }, [allFilteredMatches, displayLimit]);

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20); // Reset display limit
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20); // Reset limit
  };

  const leaguesData = useMemo(() => {
    return baseLeaguesWithMatches.map((item) => ({
      id: item.league?.id || item.id,
      name: item.league?.name || item.name,
      logo: item.league?.logo || item.logo,
    }));
  }, [baseLeaguesWithMatches]);

  // const flatMatches = useMemo(() => {
  //   return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
  //     (matches || []).map((m) => ({ ...m, league })),
  //   );
  // }, [filteredLeaguesWithMatches]);

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 md:rounded-xl px-0.5 md:px-2 py-3 bg-[var(--theme-foreground)] md:bg-[var(--theme-bg)] bg-cover bg-center bg-fixed bg-no-repeat"
      style={{
        ...themeStyle,
        backgroundImage: `url(${bgHome?.src || bgHome})`,
      }}
    >
      <div className="space-y-2 rounded-lg">
        <div className="md:kbv-wr-bg-glass-filter bg-transparent flex flex-col md:flex-row md:flex-nowrap items-center justify-between h-auto md:h-[76px] p-2 md:px-6 md:py-0 gap-4 relative rounded-tl-lg rounded-tr-lg border border-[var(--theme-border)] border-b-0">
          <div className="flex w-full md:w-[78%] items-center overflow-x-auto gap-4">
            <LobbyDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="flex w-full md:w-[10%] shrink-0 items-center justify-center md:justify-end">
            <LobbyLeagueFilterV9
              selectedIds={selectedLeagueIds}
              onChange={handleLeagueFilterChange}
              className="h-full w-full md:w-[160px]"
              themeStyle={themeStyle}
            />
          </div>
        </div>

        {loading && <StakeLoading />}
        {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
        {!loading && !error && (
          <>
            {baseLeaguesWithMatches.length === 0 ? (
              <NoData message="Không có trận đấu nào" />
            ) : filteredLeaguesWithMatches.length === 0 ? (
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            ) : (
              <LobbyMatchLeagues
                leagues={leaguesData}
                matches={visibleMatches}
                render={(match, idx) =>
                  match ? (
                    <LobbyMatch
                      key={match.id || idx}
                      match={match}
                      isOdd={idx % 2 === 0}
                    />
                  ) : null
                }
              />
            )}
            <LoadMoreButton
              isLoading={isLoadingMore}
              onClick={() => setDisplayLimit((prev) => prev + 20)}
              loadingComponent={<StakeLoading />}
              hasMore={allFilteredMatches.length > displayLimit || hasMore}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default KbvTiLeV9;
